<template>
  <v-text-field
      rounded="lg"
      :error-messages="error"
      :label="translation(translationList.postal_code, 'Cod poștal')"
      v-model="postalCode"
      :rules="postalCodeValidationRules"
      name="postal_code"
      variant="outlined"
      bg-color="surface"
      :tile="true"
      type="number"
  >
    <template v-slot:append-inner>
      <v-tooltip location="top" open-delay="500" v-if="isAutoCompleted">
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" class="cursor-pointer" aria-label="info">mdi-information</v-icon>
        </template>
        <span>
            {{ translation(translationList.postal_code_auto_completed, 'Codul poștal a fost autocompletat in baza localității, va rugăm să verificați corectitudinea acestuia') }}
        </span>
      </v-tooltip>
    </template>
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {TranslationList} from '~/types/Utils';

export default defineComponent({
  props: {
    modelValue: String as () => string | null,
    addressIndex: Number as () => number | null,
    isPostalCodeAutoCompleted: Boolean as () => boolean,
  },
  setup(props, { emit }) {
    const { modelValue, addressIndex } = toRefs(props);
    const postalCode = ref(modelValue.value ? modelValue.value : '');
    const error = ref([]);
    //isAutoCompleted needs to be with computed because it is a prop
    const isAutoCompleted = computed(() => props.isPostalCodeAutoCompleted);

    watch(modelValue, (newValue) => {
      postalCode.value = newValue;
    });

    watch(postalCode, (newValue) => {
      const isValid = validatePostalCode(newValue);
      if (isValid) {
        emit('update:modelValue', newValue);
      } else {
        emit('update:modelValue', null);
      }
      emit('validation-address-status', { field: Number.isInteger(addressIndex.value) ? `postal_code-${addressIndex.value}` : 'postal_code', isValid });
    });

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const postalCodeValidationRules = [
      (v: string) => !!v || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: string) => /^[0-9]+$/.test(v) || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
      (v: string) => (v && v.length <= 6) || translation(translationList.value.invalid_postal_code_length, 'Acest câmp trebuie să aibă maxim 6 cifre.'),
    ];

    function validatePostalCode(value: string, showErrors?: boolean): boolean {
      error.value = [];
      if (showErrors) {
        const errors = postalCodeValidationRules
            .map(rule => rule(value))
            .filter(result => result !== true) as string[];
        if (errors && errors.length > 0) {
          error.value = errors;
          return false;
        }
      }
      return postalCodeValidationRules.every(rule => rule(value) === true);
    }

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      const isValid = validatePostalCode(postalCode.value);
      emit('validation-address-status', { field: Number.isInteger(addressIndex.value) ? `postal_code-${addressIndex.value}` : 'postal_code', isValid });

      window.addEventListener('validate-address', validateEventHandler);
      window.addEventListener('validate-house', validateEventHandler);
      window.addEventListener('validate-travel', validateEventHandler);
    });

    // Listen for validate events
    const validateEventHandler = (data) => {
      if (data.detail === (Number.isInteger(addressIndex.value) ? `postal_code-${addressIndex.value}` : 'postal_code')) {
        const isValid = validatePostalCode(postalCode.value, true);
        if (isValid) {
          emit('validation-address-status', { field: Number.isInteger(addressIndex.value) ? `postal_code-${addressIndex.value}` : 'postal_code', isValid });
        }
      }
    };

    return {
      postalCode,
      postalCodeValidationRules,
      translationList,
      translation,
      error,
      isAutoCompleted
    };
  },
});
</script>

<style scoped>
</style>